import React, { useMemo, useState, useEffect } from "react";
import PropTypes from 'prop-types';
import classNames from "classnames";
import { Icon, Position } from "@blueprintjs/core";
import moment from "moment";
import { Tooltip } from "components/tooltip";
import './Key.css';

function getIconDisplayConfig({ icons, colours, notes, maxIcons, maxToolTipItems, sortedEvents }) {
    const isDayView = maxIcons === 1;
    const hasIcons = icons.length > 0;
    const hasNotes = notes && notes.length > 0;
    const showSingleCenteredNote = isDayView && !hasIcons && notes.length === 1;

    if (!hasIcons && !hasNotes) return { type: 'none' };
    if (showSingleCenteredNote) return { type: 'singleNote' };

    const eventsToShow = sortedEvents.slice(0, maxToolTipItems);
    const notesToShow = notes.slice(0, Math.max(0, maxToolTipItems - eventsToShow.length));
    const showNoteIcon = notesToShow.length > 0;

    if (isDayView) {
        return getDayViewConfig(icons, colours, showNoteIcon);
    }
    return getMultiIconConfig(icons, colours, maxIcons, showNoteIcon);
}

function getDayViewConfig(icons, colours, showNoteIcon) {
    if (icons.length > 0 && showNoteIcon) {
        return {
            type: 'icons',
            iconsToShow: icons.slice(0, 1),
            colorsToShow: colours.slice(0, 1),
            showNote: false,
            showEllipsis: true
        };
    }
    if (icons.length > 0) {
        return {
            type: 'icons',
            iconsToShow: icons.slice(0, 1),
            colorsToShow: colours.slice(0, 1),
            showNote: false,
            showEllipsis: false
        };
    }
    if (showNoteIcon) {
        return {
            type: 'icons',
            iconsToShow: [],
            colorsToShow: [],
            showNote: true,
            showEllipsis: false
        };
    }
    return { type: 'none' };
}

function getMultiIconConfig(icons, colours, maxIcons, showNoteIcon) {
    let iconsToShow = icons.slice(0, maxIcons);
    let colorsToShow = colours.slice(0, maxIcons);
    let showNote = false;
    let showEllipsis = false;

    if (iconsToShow.length < maxIcons && showNoteIcon) {
        showNote = true;
    }
    if (icons.length + (showNoteIcon ? 1 : 0) > maxIcons) {
        iconsToShow = icons.slice(0, maxIcons - 1);
        colorsToShow = colours.slice(0, maxIcons - 1);
        showEllipsis = true;
        showNote = false;
    }
    return {
        type: 'icons',
        iconsToShow,
        colorsToShow,
        showNote,
        showEllipsis
    };
}

function renderMonthIconRows({ iconsToShow, colorsToShow, showNote, showEllipsis, size, iconColour }) {
    const maxIconsPerRow = 5;
    const firstRowIcons = iconsToShow.slice(0, maxIconsPerRow);
    const secondRowIcons = iconsToShow.slice(maxIconsPerRow);
    const firstRowColors = colorsToShow.slice(0, maxIconsPerRow);
    const secondRowColors = colorsToShow.slice(maxIconsPerRow);
    const showNoteInFirstRow = showNote && firstRowIcons.length < maxIconsPerRow;
    return (
        <div className="keyblock-icon-container">
            <div className="keyblock-icon-row">
                {firstRowIcons.map((iconClass, idx) =>
                    <i
                        key={`key-block-${iconClass}-${idx}`}
                        className={`custom-icon custom-icon-${iconClass} keyblock-icon`}
                        style={{
                            fontSize: size * 1.1,
                            color: firstRowColors[idx] || iconColour
                        }}
                    />
                )}
                {showNoteInFirstRow && (
                    <Icon
                        icon="document"
                        className="keyblock-note-icon cell-note-icon"
                        style={{
                            color: "#1c8cf9",
                            marginLeft: 2,
                            marginRight: 2
                        }}
                        aria-label="Note"
                    />
                )}
            </div>
            <div className="keyblock-icon-row keyblock-icon-row-bottom">
                {secondRowIcons.map((iconClass, idx) =>
                    <i
                        key={`key-block-${iconClass}-second-row-${idx}`}
                        className={`custom-icon custom-icon-${iconClass} keyblock-icon`}
                        style={{
                            fontSize: size * 1.1,
                            color: secondRowColors[idx] || iconColour
                        }}
                    />
                )}
                {!showNoteInFirstRow && showNote && (
                    <Icon
                        icon="document"
                        className="keyblock-note-icon cell-note-icon"
                        style={{
                            color: "#1c8cf9",
                            marginLeft: 2,
                            marginRight: 2
                        }}
                        aria-label="Note"
                    />
                )}
                {showEllipsis && (
                    <span
                        key="key-block-ellipsis"
                        className="keyblock-ellipsis">{'\u2026'}
                    </span>
                )}
            </div>
        </div>
    );
}

function renderSingleNoteIcon() {
    return (
        <div className={classNames("keyblock-icon-container", "keyblock-icon-container-single", "keyblock-icon-center")}> 
            <Icon
                icon="document"
                className="keyblock-note-icon cell-note-icon"
                style={{
                    color: "#1c8cf9",
                    marginLeft: 2,
                    marginRight: 2
                }}
                aria-label="Note"
            />
        </div>
    );
}

function renderIconsRow({ iconsToShow, colorsToShow, showNote, showEllipsis, size, iconColour, isDayView }) {
    const isSingleIcon = iconsToShow.length === 1;
    return (
        <div
            className={classNames(
                "keyblock-icon-container",
                "keyblock-icon-container-single",
                {
                    "keyblock-icon-center": isDayView && isSingleIcon
                }
            )}
        >
            {iconsToShow.map((iconClass, idx) => (
                <i
                    key={`${iconClass}-${idx} || iconColour}`}
                    className={`custom-icon custom-icon-${iconClass} keyblock-icon`}
                    style={{
                        fontSize: size * 1.1,
                        color: colorsToShow[idx] || iconColour
                    }}
                />
            ))}
            {showEllipsis && (
                <span className="keyblock-ellipsis">{'\u2026'}</span>
            )}
            {showNote && (
                <Icon
                    icon="document"
                    className="keyblock-note-icon cell-note-icon"
                    style={{
                        color: "#1c8cf9",
                        marginLeft: 2,
                        marginRight: 2
                    }}
                    aria-label="Note"
                />
            )}
        </div>
    );
}

function calculateColours(cls) {
    if (!Array.isArray(cls)) {
        return cls;
    }
    const TOP = 0;
    const RIGHT = 1;
    const BOTTOM = 2;
    const LEFT = 3;
    return `${cls[TOP]} ${cls[RIGHT]} ${cls[BOTTOM]} ${cls[LEFT]}`;
}

export function ListingKeyBlock(
    {
        size = 10,
        rounded = false,
        icon = null,
        maxIcons = 1,
        selectable = false,
        selected = false,
        loading = false,
        onClick = () => { },
        iconColour = "#FFFFFF",
        hideBorder = false,
        toolTipText = "",
        tooltipPosition = Position.TOP,
        vorRange = false,
        events = [],
        notes = [],
        periodType = "day",
        className = "",
        colours
    }
) {
    const maxToolTipItems = 10;

    const [sortedEvents, setSortedEvents] = useState([]);
    const [iconsAndColours, setIconsAndColours] = useState(null);

    useEffect(() => {
        if (events) {
            let eventsInOrder = Array.isArray(events)
                ? [...events].sort((a, b) => {
                    let aEventDt = moment(a.eventDate).startOf('day');
                    let bEventDt = moment(b.eventDate).startOf('day');
                    if (!aEventDt.isSame(bEventDt)) {
                        return aEventDt.isBefore(bEventDt) ? -1 : 1;
                    }
                    if (a.priority !== b.priority) {
                        return a.priority - b.priority;
                    }
                    if (a.eventTypeName !== b.eventTypeName) {
                        return (a.eventTypeName || '').localeCompare(b.eventTypeName || '');
                    }
                    return (a.eventStatusName || '').localeCompare(b.eventStatusName || '');
                })
                : [];

            setSortedEvents(eventsInOrder);
        }
    }, [events]);

    useEffect(() => {
        if (!sortedEvents || sortedEvents.length < 1) {
            return;
        }

        let icons = [];
        if (sortedEvents.length > 0) {
            icons = sortedEvents.map(ev => ev.eventIcon).filter(Boolean);
        }

        let coloursValue = [];
        if (sortedEvents.length > 0) {
            coloursValue =  sortedEvents.map(ev => ev.eventStatusColour || iconColour);
        }

        setIconsAndColours({
            icons: icons,
            colours: coloursValue
        });
    }, [sortedEvents]);

    function handleClick() {
        onClick();
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    }

    const getTooltipContent = useMemo(() => {
        if ((!Array.isArray(sortedEvents) || sortedEvents.length === 0) && (!notes || notes.length === 0)) {
            return null;
        }
        
        const sortedNotes = Array.isArray(notes)
            ? [...notes].sort((a, b) => moment(a.date).diff(moment(b.date)))
            : [];

        const eventsToShow = sortedEvents.slice(0, maxToolTipItems);
        const notesToShow = sortedNotes.slice(0, Math.min(sortedNotes.length, maxToolTipItems - eventsToShow.length));

        let eventsAndNotesLabel = '';
        if (sortedEvents.length > 0 && notes.length > 0) {
            eventsAndNotesLabel = 'events and notes';
        } else if (sortedEvents.length > 0) {
            eventsAndNotesLabel = 'events';
        } else {
            eventsAndNotesLabel = 'notes';
        }

        return (
            <div>
                <ul>
                    {eventsToShow.map(ev => (
                        <li key={ev.eventId}>
                            {maxIcons === 1 ? ev.eventTypeName : (moment(ev.eventDate).format("DD MMM") +  " "+ev.eventTypeName)}{" "}
                            <span className="fleet-main-view-span">
                                ({ev.eventStatusName})
                                {ev.vorStartDate && ev.vorEndDate &&
                                    <> &middot; VOR: {moment(ev.vorStartDate).format("DD MMM")}-{moment(ev.vorEndDate).format("DD MMM")}</>
                                }
                            </span>
                        </li>                        
                    ))}
                    {notesToShow.map(no => (
                        <li key={no.date || no.note}>
                            {maxIcons === 1 ? "" : moment(no.date).format("DD MMM") + " "}
                            <Icon
                                icon="document"
                                className="keyblock-note-icon"
                                style={{color: "#888"}}
                                aria-label="Note"
                            />{' '}
                            <span className="tooltip-note-text">{no.note}</span>
                        </li>
                    ))}
                </ul>
                {(sortedEvents.length + notes.length) > maxToolTipItems && (
                    <div style={{ marginTop: "8px", fontWeight: 600, textAlign: "left", cursor: "pointer" }}>
                        ... click {periodType} to see all {sortedEvents.length + notes.length} {eventsAndNotesLabel}
                    </div>
                )}
            </div>
        );
    }, [sortedEvents, maxToolTipItems, notes, maxIcons, periodType]);

    const getIconRows = useMemo(() => {
        const icons = iconsAndColours?.icons || [];
        const coloursValue = iconsAndColours?.colours || [];
        const config = getIconDisplayConfig({
            icons,
            colours: coloursValue,
            notes,
            maxIcons,
            maxToolTipItems,
            sortedEvents
        });
        if (config.type === 'none') return null;
        if (config.type === 'singleNote') return renderSingleNoteIcon();
        if (periodType === "month") {
            return renderMonthIconRows({
                ...config,
                size,
                iconColour
            });
        }
        return renderIconsRow({
            ...config,
            size,
            iconColour,
            isDayView: maxIcons === 1
        });
    }, [periodType, sortedEvents, size, iconColour, maxIcons, iconsAndColours, notes]);

    return (
        <div
            className={classNames("key-item-container", className, {
                "vor-range": vorRange
            })}
            style={{ width: size * 2, height: size * 2 }}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={-1}
        >
            <Tooltip
                position={tooltipPosition}
                content={getTooltipContent || ""}
                disabled={!getTooltipContent}
                usePortal={false}
            >
                <div
                    className={classNames(
                        "fleet-key-block",
                        "key-item",
                        {
                            "loading": loading,
                            "rounded": rounded,
                            "empty": colours?.length === 0,
                            "selectable": selectable,
                            "selected": selected,
                            "key-item-border": !hideBorder,
                            "fleet-key-item-border": true,
                            "vor-range": vorRange,
                            "day-week-no-vor": periodType !== "month" && !vorRange
                        }
                    )}
                    style={{
                        borderWidth: size,
                        borderColor: calculateColours(colours)
                    }}
                >
                    {getIconRows}
                </div>
            </Tooltip>
        </div>
    );
}

ListingKeyBlock.propTypes = {
    colours: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
    size: PropTypes.number,
    rounded: PropTypes.bool,
    maxIcons: PropTypes.number,
    selectable: PropTypes.bool,
    selected: PropTypes.bool,
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    iconColour: PropTypes.string,
    hideBorder: PropTypes.bool,
    toolTipText: PropTypes.string,
    tooltipPosition: PropTypes.string,
    vorRange: PropTypes.bool,
    events: PropTypes.array,
    notes: PropTypes.array,
    periodType: PropTypes.string,
    className: PropTypes.string
};